/*******************************************************************************
* @license
* Copyright (c) 2016 - present Accedo. All Rights Reserved.
* 
* This source code, its use, and distribution, is subject to the terms
* and conditions of the applicable license agreement.
* 
* VDK Web ProgramGuide Component (@accedo/vdkweb-epg) 7.2.8
* 
*******************************************************************************/
"use strict";

function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _timeHelper = require("../utils/timeHelper");
var _transition = require("../utils/transition");
var _programLane = _interopRequireDefault(require("./programLane.scss"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
/**
 * A lane for listing programs positioned based on
 * their start/end time.
 *
 * Will apply the positioning to its child components
 * which ought to be Program components.
 *
 * @param {object} props See propTypes
 * @returns {ProgramLane} A ProgramLane component
 */
var ProgramLane = /*#__PURE__*/_react["default"].memo(function (_ref) {
  var children = _ref.children,
    startTime = _ref.startTime,
    minuteToPixelRatio = _ref.minuteToPixelRatio,
    animation = _ref.animation,
    _ref$vertical = _ref.vertical,
    vertical = _ref$vertical === void 0 ? true : _ref$vertical,
    style = _ref.style,
    _ref$theme = _ref.theme,
    theme = _ref$theme === void 0 ? _programLane["default"] : _ref$theme,
    rtl = _ref.rtl,
    epgEndTime = _ref.epgEndTime,
    epgStartTime = _ref.epgStartTime;
  var wrappedChildren = _react["default"].Children.map(children, function (child) {
    var _child$props = child.props,
      programEndTime = _child$props.endTime,
      programStartTime = _child$props.startTime,
      _programStyle = _child$props.style;
    var startDateForDuration = programStartTime < epgStartTime ? epgStartTime : programStartTime;
    var endDateForDuration = programEndTime > epgEndTime ? epgEndTime : programEndTime;
    if (programEndTime < epgStartTime) {
      return null;
    }
    var offset = Math.round((0, _timeHelper.minutesToPixels)((0, _timeHelper.timeDiffInMinutes)(startTime, startDateForDuration), minuteToPixelRatio));
    var duration = (endDateForDuration - startDateForDuration) / (1000 * 60);
    var height = vertical ? (0, _timeHelper.minutesToPixels)(duration, minuteToPixelRatio) : '100%';
    var width = !vertical ? (0, _timeHelper.minutesToPixels)(duration, minuteToPixelRatio) : '100%';
    var transform = vertical ? "translateY(".concat(offset, "px)") : "translateX(".concat(rtl ? -offset : offset, "px)");
    var programStyle = _objectSpread({
      height: height,
      width: width,
      transform: transform,
      WebkitTransform: transform,
      transition: (0, _transition.animationToTransition)(animation),
      position: 'absolute',
      display: 'inline-block',
      boxSizing: 'border-box'
    }, _programStyle);
    return /*#__PURE__*/_react["default"].cloneElement(child, {
      style: programStyle
    });
  });
  return /*#__PURE__*/_react["default"].createElement("div", {
    className: theme.programLane,
    style: style
  }, /*#__PURE__*/_react["default"].createElement("div", {
    className: theme.innerContainer
  }, wrappedChildren));
});
ProgramLane.propTypes = {
  /**
   * Animation settings deciding enabled,
   * speed and type (e.g. linear, ease-out)
   */
  animation: _propTypes["default"].shape({
    disabled: _propTypes["default"].bool,
    speed: _propTypes["default"].number,
    type: _propTypes["default"].string
  }),
  /**
   * The children of the list,
   * expected to be Program elements
   */
  children: _propTypes["default"].node,
  /**
   * The EPG endTime to check against
   */
  epgEndTime: _propTypes["default"].number,
  /**
   * The EPG startTime to check against
   */
  epgStartTime: _propTypes["default"].number,
  /**
   * The ratio deciding how many pixels
   * one minute will represent
   */
  minuteToPixelRatio: _propTypes["default"].number,
  /** Right to left */
  rtl: _propTypes["default"].bool,
  /**
   * The total start time of the program list,
   * programs are positioned in relation to this time
   * depending on the program's start time.
   */
  startTime: _propTypes["default"].number,
  style: _propTypes["default"].object,
  /** Classname API for theming */
  theme: _propTypes["default"].shape({
    program: _propTypes["default"].string,
    programLane: _propTypes["default"].string,
    timelineContainer: _propTypes["default"].string
  }),
  /**
   * If the programs are being layed out
   * vertically in a channel lane
   */
  vertical: _propTypes["default"].bool
};

// TODO: Apply a better rendering approach than limiting updates to these keys
var _default = /*#__PURE__*/_react["default"].memo(ProgramLane, function (_ref2, _ref3) {
  var prevMinuteToPixelRatio = _ref2.minuteToPixelRatio,
    prevStartTime = _ref2.startTime,
    prevCurrentTime = _ref2.currentTime;
  var minuteToPixelRatio = _ref3.minuteToPixelRatio,
    startTime = _ref3.startTime,
    currentTime = _ref3.currentTime;
  return prevMinuteToPixelRatio === minuteToPixelRatio && prevStartTime === startTime && prevCurrentTime === currentTime;
});
exports["default"] = _default;